import { h } from 'preact';
import Section from 'components/bolt/section/section';
import { navigateInternally } from 'utils/bolt';
import { sortObjectByOrder, joinClasses, debounce } from 'utils/utils';
import { smoothScrollTo } from 'utils/smoothScroll';
import s from 'components/bolt/toutHorizontal/toutHorizontal.sss';
import ArrowDown from 'components/bolt/icons/arrowDown';
import HTML from 'components/core/html/html';
import Store from 'store/store';

const DISPLAYED_TOUTS = 3;

export default class ToutHorizontal extends Section {
	constructor(props) {
		super(props);

		this.state = {
			usingScrollWheel: true,
			touts: this.getTouts(props),
		};

		this.onToutsContainerScroll = debounce(this.onToutsContainerScroll.bind(this), 20);
		this.move = this.move.bind(this);
		this.snapToClosestTout = this.snapToClosestTout.bind(this);
		this.setUsingScrollWheel = this.setUsingScrollWheel.bind(this);
		this.setNotUsingScrollWheel = this.setNotUsingScrollWheel.bind(this);
	}

	componentDidMount() {
		this.onToutsContainerScroll();
		window.addEventListener('resize', this.onToutsContainerScroll);
	}

	componentWillReceiveProps(nextProps) {
		if (nextProps.id !== this.props.id) {
			this.setState({ touts: this.getTouts(nextProps) });
		}
	}

	componentWillUnmount() {
		window.clearTimeout(this.snapTimeout);
		window.removeEventListener('resize', this.onToutsContainerScroll);
	}

	getTouts(props = this.props) {
		const pageData = props?.appData?.pages?.[props.id]?.data;
		return sortObjectByOrder(pageData?.carousel, (tout, key) => ({ ...tout, key }));
	}

	onToutsContainerScroll() {
		clearTimeout(this.snapTimeout);
		if (this.scrollingTo) {
			return;
		}
		let el = this.$toutsContainer;
		let maxScroll = el.scrollWidth;
		// Elements are duplicated three times - keep the scroll in the middle third to be able to scroll left and right
		let repeatingSize = maxScroll / 3;
		if (el.scrollLeft < repeatingSize) {
			el.scrollLeft += repeatingSize;
		} else if (el.scrollLeft > maxScroll - repeatingSize) {
			el.scrollLeft -= repeatingSize;
		}
		if (this.state.usingScrollWheel) {
			this.snapTimeout = setTimeout(this.snapToClosestTout, 500);
		}
	}

	getClosestChild(parent, position) {
		let closest;
		let closestDist = Infinity;
		let scroll = parent.scrollLeft;
		[...parent.children].find(el => {
			let elCenter = el.offsetLeft + el.offsetWidth * .5 - scroll;
			let dist = Math.abs(elCenter - position);
			if (dist >= closestDist) {
				return true;
			}
			closest = el;
			closestDist = dist;
		});
		return closest;
	}

	onClick(e) {
		let link = e.target.closest('a');
		if (!link) {
			return;
		}
		let href = link.getAttribute('href');
		if (href.startsWith('#$')) {
			e.preventDefault();
			let page = href.slice(2).trim();
			let navOptions = {};
			if (page.includes('/?')) {
				let [pageId, qs] = page.split('/?');
				page = pageId;
				navOptions.queryString = '?' + qs;
			}
			navigateInternally(page, navOptions);
		}
		let trackAction = link.getAttribute('data-track-action');
		if (trackAction) {
			window.firePowsterCustomEvent?.(trackAction, link.getAttribute('data-track-category') || 'tout', link.getAttribute('data-track-label'));
		}
	}

	scrollTo(destination, checkScrollPositionAfter) {
		if (!destination) {
			return;
		}
		clearTimeout(this.snapTimeout);
		smoothScrollTo(
			this.$toutsContainer,
			destination,
			200,
			{
				direction: 'horizontal',
				relativeTo: 'middle',
				callback: () => {
					this.scrollingTo = null;
					if (checkScrollPositionAfter) {
						setTimeout(this.onToutsContainerScroll, 0);
					}
				}
			}
		);
		this.scrollingTo = destination;
	}

	snapToClosestTout() {
		let container = this.$toutsContainer;
		this.scrollTo(this.getClosestChild(container, container.offsetWidth * .5));
	}

	setUsingScrollWheel() {
		this.setState({ usingScrollWheel: true });
	}
	setNotUsingScrollWheel() {
		this.setState({ usingScrollWheel: false });
	}

	move(direction) {
		let el = this.$toutsContainer;
		if (!el) {
			return;
		}
		let currentElement = this.scrollingTo || this.getClosestChild(el, el.offsetWidth * .5);
		if (!currentElement) {
			return;
		}
		this.scrollTo(direction < 0 ? currentElement.previousElementSibling : currentElement.nextElementSibling, true);
	}

	trackToutClick(data) {
		Store.emit('TOUT_CLICK', 'horizontal', 'tout', `${data?.title} - ${data?.button}`);
	}
	triggerButtonClick(data) {
		const { path } = data;
		this.trackToutClick(data);
		navigateInternally(path, { animate: true });
	}

	renderTout(data, index, keySuffix) {
		const { title, description, src, alt, button } = data;
		const imageOnly = !title && !description && !button;

		let buttonElement;
		if (button) {
			if (/^(https?:)?\/\//.test(data.path) || /^mailto:/.test(data.path)) {
				buttonElement = (
					<a class={joinClasses(s.button, 'mediumB')} href={data.path} target={!data.sameWindow && '_blank'} onClick={() => this.trackToutClick(data)}>
						{button}
					</a>
				);
			} else {
				buttonElement = (
					<button class={joinClasses(s.button, 'mediumB')} onClick={() => this.triggerButtonClick(data)}>
						{button}
					</button>
				);
			}
		}

		return (
			<div class={s.tout} key={(data.key || index) + (keySuffix || '')}>
				<div class={s.toutContent}>
					<div class={s.toutStuff}>
						{src && <img crossOrigin="anonymous" class={joinClasses(s.image, imageOnly && s.imageOnly)} alt={alt} src={src} />}
						{title && <HTML tag="h3" markdown onClickCapture={this.onClick} content={title} class={joinClasses(s.toutTitle, 'largeB')} />}
						{description && (
							<HTML
								tag="h4"
								markdown
								content={description}
								class={joinClasses(s.toutDescription, s.showGradient, button && s.flex, 'large')}
							/>
						)}
						{buttonElement}
					</div>
				</div>
			</div>
		);
	}

	renderTouts() {
		const { touts } = this.state;
		const tooManyTouts = touts.length > DISPLAYED_TOUTS;

		let content = touts.map((tout, index) => this.renderTout(tout, index));
		if (tooManyTouts || this.props.client?.deviceWidthSize === 'mobile' && touts.length > 1) {
			// Render duplicate items for infinite scrolling
			content.push(
				...touts.map((tout, index) => this.renderTout(tout, index, 'duplicate1')),
				...touts.map((tout, index) => this.renderTout(tout, index, 'duplicate2')),
			);
		}

		return (
			<div class={s.toutsWrapper}>
				<div
					class={s.toutsContainer}
					onScroll={this.onToutsContainerScroll}
					onMouseWheel={this.setUsingScrollWheel}
					onTouchStart={this.setNotUsingScrollWheel}
					onKeyDown={this.setNotUsingScrollWheel}
					ref={e => this.$toutsContainer = e}
				>
					{content}
				</div>
				{tooManyTouts && this.renderArrows()}
			</div>
		);
	}

	renderArrows() {
		return (
			<div class={s.arrows}>
				<button class={joinClasses(s.arrow, s.left)} onClick={() => this.move(-1)}>
					<ArrowDown />
				</button>
				<button class={joinClasses(s.arrow, s.right)} onClick={() => this.move(1)}>
					<ArrowDown />
				</button>
			</div>
		);
	}

	render({ id }) {
		const pageData = this.props?.appData?.pages[this.props.id].data || {};

		const { title, description } = pageData;
		const isSection = this.props?.appData?.pages[id].section || false;

		return (
			<div class={joinClasses(s.wrapper, isSection && s.section, this.state.usingScrollWheel && s.usingScrollWheel)} data-section={id}>
				{this.renderBgContent()}
				{title && <HTML tag="h1" markdown onClickCapture={this.onClick} content={title} class={joinClasses(s.title, 'h3')} />}
				{description && <HTML tag="h2" markdown content={description} class={joinClasses(s.description, 'xlarge')} />}
				{this.renderTouts()}
				{this.renderOverlayedContent()}
			</div>
		);
	}
}
